import React, { createContext, useContext, useState, useCallback } from 'react';
import { resumeApi, jobApi, applicationApi } from '../services/api';

const CacheContext = createContext();

export const CacheProvider = ({ children }) => {
  // Cache state
  const [resumeCache, setResumeCache] = useState({
    list: null,
    timestamp: null,
  });
  const [jobCache, setJobCache] = useState({
    list: null,
    timestamp: null,
  });
  const [applicationCache, setApplicationCache] = useState({
    list: null,
    timestamp: null,
  });

  // Cache duration in milliseconds (5 minutes)
  const CACHE_DURATION = 5 * 60 * 1000;

  // Check if cache is valid
  const isCacheValid = (timestamp) => {
    if (!timestamp) return false;
    return Date.now() - timestamp < CACHE_DURATION;
  };

  // Resume cache methods
  const getResumes = useCallback(async (forceRefresh = false) => {
    if (!forceRefresh && resumeCache.list && isCacheValid(resumeCache.timestamp)) {
      return resumeCache.list;
    }

    try {
      const response = await resumeApi.getAllResumes();
      const resumes = response.data;
      setResumeCache({
        list: resumes,
        timestamp: Date.now(),
      });
      return resumes;
    } catch (error) {
      console.error('Error fetching resumes:', error);
      throw error;
    }
  }, [resumeCache]);

  const invalidateResumeCache = useCallback(() => {
    setResumeCache({
      list: null,
      timestamp: null,
    });
  }, []);

  // Job cache methods
  const getJobs = useCallback(async (forceRefresh = false) => {
    if (!forceRefresh && jobCache.list && isCacheValid(jobCache.timestamp)) {
      return jobCache.list;
    }

    try {
      const response = await jobApi.getAllJobs();
      const jobs = response.data;
      setJobCache({
        list: jobs,
        timestamp: Date.now(),
      });
      return jobs;
    } catch (error) {
      console.error('Error fetching jobs:', error);
      throw error;
    }
  }, [jobCache]);

  const invalidateJobCache = useCallback(() => {
    setJobCache({
      list: null,
      timestamp: null,
    });
  }, []);

  // Application cache methods
  const getApplications = useCallback(async (forceRefresh = false) => {
    if (!forceRefresh && applicationCache.list && isCacheValid(applicationCache.timestamp)) {
      return applicationCache.list;
    }

    try {
      const response = await applicationApi.getAllApplications();
      const applications = response.data;
      setApplicationCache({
        list: applications,
        timestamp: Date.now(),
      });
      return applications;
    } catch (error) {
      console.error('Error fetching applications:', error);
      throw error;
    }
  }, [applicationCache]);

  const invalidateApplicationCache = useCallback(() => {
    setApplicationCache({
      list: null,
      timestamp: null,
    });
  }, []);

  // Invalidate all caches
  const invalidateAllCaches = useCallback(() => {
    invalidateResumeCache();
    invalidateJobCache();
    invalidateApplicationCache();
  }, [invalidateResumeCache, invalidateJobCache, invalidateApplicationCache]);

  const value = {
    // Resume methods
    getResumes,
    invalidateResumeCache,
    
    // Job methods
    getJobs,
    invalidateJobCache,
    
    // Application methods
    getApplications,
    invalidateApplicationCache,
    
    // General methods
    invalidateAllCaches,
  };

  return <CacheContext.Provider value={value}>{children}</CacheContext.Provider>;
};

export const useCache = () => {
  const context = useContext(CacheContext);
  if (!context) {
    throw new Error('useCache must be used within a CacheProvider');
  }
  return context;
};